/**
 * BillingSection - "Billing" form section molecule.
 *
 * Renders the framed section card that owns billing categorization +
 * billing address + billing contact + billing notes. Used by the
 * JobFormTemplate for both work order and inspection forms so the
 * create experience is identical between the two.
 *
 * The billing address sub-block is read-only by default and auto-fills
 * from the selected company; an "Edit" toggle unlocks the inputs and
 * a confirmation note is shown so managers know that editing here will
 * propagate the change up to the company record.
 *
 * Composition:
 *	 - BillingInfoTagSelector (multi-checkbox grid of canonical tags)
 *	 - BillingAddressBlock (Edit-toggle wrapping AddressInput + city/state/zip)
 *	 - Billing Phone + Email row
 *	 - Billing Notes textarea
 */
import React from 'react';
import { Checkbox } from '../../atoms';
import { AddressInput } from '../AddressInput';
import { SectionCard } from '../SectionCard';
import { formatPhone } from '../../../utils/formatPhone';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

export interface BillingSectionProps {
	/** Multi-select billing tag values currently selected. */
	billingInfo: string[];
	/** Toggle a single tag on/off. */
	onBillingInfoToggle: (option: string) => void;
	/** Canonical list of selectable tags rendered as checkboxes. */
	billingInfoOptions: string[];
	/** Inline error for the tag group — the server requires at least one tag
	 *  on a standalone work order, so the form validates it before submitting. */
	billingInfoError?: string;

	/** Whether the address sub-block is unlocked for editing. */
	isAddressEditable: boolean;
	/** Called to flip the address sub-block into edit mode. */
	onEditAddress: () => void;

	billingName: string;
	onBillingNameChange: (value: string) => void;
	billingAddress: string;
	onBillingAddressChange: (value: string) => void;
	billingAddressLine2: string;
	onBillingAddressLine2Change: (value: string) => void;
	billingCity: string;
	onBillingCityChange: (value: string) => void;
	billingState: string;
	onBillingStateChange: (value: string) => void;
	billingZip: string;
	onBillingZipChange: (value: string) => void;

	billingPhone: string;
	onBillingPhoneChange: (value: string) => void;
	billingEmail: string;
	onBillingEmailChange: (value: string) => void;
	billingNotes: string;
	onBillingNotesChange: (value: string) => void;

	/** Standard styling tokens. */
	labelClass: string;
	inputClass: string;
	normalBorder: string;
	/** Optional extra classes appended to the section root. */
	className?: string;
}

/* ============================================
 * COMPONENT
 * ============================================ */

export const BillingSection: React.FC<BillingSectionProps> = ({
	billingInfo,
	onBillingInfoToggle,
	billingInfoOptions,
	billingInfoError,
	isAddressEditable,
	onEditAddress,
	billingName,
	onBillingNameChange,
	billingAddress,
	onBillingAddressChange,
	billingAddressLine2,
	onBillingAddressLine2Change,
	billingCity,
	onBillingCityChange,
	billingState,
	onBillingStateChange,
	billingZip,
	onBillingZipChange,
	billingPhone,
	onBillingPhoneChange,
	billingEmail,
	onBillingEmailChange,
	billingNotes,
	onBillingNotesChange,
	labelClass,
	inputClass,
	normalBorder,
	className = '',
}) => {
	/* The read-only state shows the auto-filled values in disabled
	   inputs; switching to editable swaps the address line for an
	   AddressInput typeahead so managers can update the company's
	   billing address from this surface. */
	const readOnlyInputClass = `${inputClass} ${normalBorder} ${!isAddressEditable ? 'bg-gray-50 cursor-not-allowed' : ''}`;

	return (
		<SectionCard title="Billing" className={`billing-section ${className}`} dataRef="molecule-billing-section">
			<div className="billing-section__body space-y-16px">
				{/* Billing Info — multi-select tag grid. The same canonical
				    list is shared across work orders and inspections. */}
				{/* id on the group, not on any one checkbox: the form's error
				    summary links to it, and the target of "Pick at least one
				    billing classification" is the whole set of choices. tabIndex
			    lets the link's focus() land here too, so the next Tab goes
			    into the first checkbox instead of back up to the banner. */}
				<div id="billingInfo" tabIndex={-1} className="billing-section__tags focus:outline-none">
					<label className={labelClass}>Billing Info<span className="text-red-500"> *</span></label>
					<div className={`grid grid-cols-2 tablet:grid-cols-3 gap-8px mt-4px ${billingInfoError ? 'border border-red-500 rounded-md p-8px' : ''}`}>
						{billingInfoOptions.map((option) => (
							<Checkbox key={option} checked={billingInfo.includes(option)} onChange={() => onBillingInfoToggle(option)} label={option} className="billing-section__tag items-center" dataRef="molecule-billing-section-tag" />
						))}
					</div>
					{billingInfoError && <p className="billing-section__error mt-4px text-14px text-red-600">{billingInfoError}</p>}
				</div>

				{/* Billing Address — auto-filled from company; "Edit" unlocks
				    and warns that the change propagates to the company record. */}
				<div>
					<div className="flex items-center justify-between mb-4px">
						<label className={labelClass}>Billing Address</label>
						{!isAddressEditable ? (
							<button type="button" onClick={onEditAddress} className="min-h-44px inline-flex items-center text-12px font-sans font-medium text-brand-primary hover:text-brand-primary-hover transition-colors">Edit</button>
						) : (
							<span className="text-12px font-sans text-amber-600">Editing will update the company&apos;s billing address on file</span>
						)}
					</div>

					{/* Billing Name */}
					<div className="mb-8px">
						<label htmlFor="billingName" className={labelClass}>Billing Name</label>
						<input id="billingName" type="text" value={billingName} onChange={(e) => onBillingNameChange(e.target.value)} readOnly={!isAddressEditable} className={readOnlyInputClass} placeholder="Name for invoice" />
					</div>

					{/* Billing Address Line 1 + Line 2 */}
					<div className="grid grid-cols-1 tablet:grid-cols-2 gap-8px mb-8px">
						<div>
							<label htmlFor="billingAddress" className={labelClass}>Address</label>
							{isAddressEditable ? (
								<AddressInput id="billingAddress" value={billingAddress} onChange={onBillingAddressChange} onSelect={(parsed) => { onBillingAddressChange(parsed.street); onBillingCityChange(parsed.city); onBillingStateChange(parsed.state); onBillingZipChange(parsed.zip); }} placeholder="Start typing billing address..." />
							) : (
								<input id="billingAddress" type="text" value={billingAddress} readOnly className={`${inputClass} ${normalBorder} bg-gray-50 cursor-not-allowed`} placeholder="Street address" />
							)}
						</div>
						<div>
							<label htmlFor="billingAddressLine2" className={labelClass}>Address Line 2</label>
							<input id="billingAddressLine2" type="text" value={billingAddressLine2} onChange={(e) => onBillingAddressLine2Change(e.target.value)} readOnly={!isAddressEditable} className={readOnlyInputClass} placeholder="Suite, unit, floor..." />
						</div>
					</div>

					{/* Billing City, State, ZIP row */}
					<div className="grid grid-cols-2 tablet:grid-cols-3 gap-8px mb-8px">
						<div className="col-span-2 tablet:col-span-1">
							<label htmlFor="billingCity" className={labelClass}>City</label>
							<input id="billingCity" type="text" value={billingCity} onChange={(e) => onBillingCityChange(e.target.value)} readOnly={!isAddressEditable} className={readOnlyInputClass} placeholder="City" />
						</div>
						<div>
							<label htmlFor="billingState" className={labelClass}>State</label>
							<input id="billingState" type="text" value={billingState} onChange={(e) => onBillingStateChange(e.target.value)} readOnly={!isAddressEditable} maxLength={2} className={readOnlyInputClass} placeholder="ST" />
						</div>
						<div>
							<label htmlFor="billingZip" className={labelClass}>ZIP</label>
							<input id="billingZip" type="text" value={billingZip} onChange={(e) => onBillingZipChange(e.target.value)} readOnly={!isAddressEditable} maxLength={10} className={readOnlyInputClass} placeholder="ZIP" />
						</div>
					</div>
				</div>

				{/* Billing Phone + Email row */}
				<div className="grid grid-cols-1 tablet:grid-cols-2 gap-16px">
					<div>
						<label htmlFor="billingPhone" className={labelClass}>Billing Phone</label>
						<input id="billingPhone" type="tel" value={billingPhone} onChange={(e) => onBillingPhoneChange(formatPhone(e.target.value))} className={`${inputClass} ${normalBorder}`} placeholder="(555) 123-4567" />
					</div>
					<div>
						<label htmlFor="billingEmail" className={labelClass}>Billing Email</label>
						<input id="billingEmail" type="email" value={billingEmail} onChange={(e) => onBillingEmailChange(e.target.value)} className={`${inputClass} ${normalBorder}`} placeholder="billing@company.com" />
					</div>
				</div>

				{/* Billing Notes — free-text */}
				<div>
					<label htmlFor="billingNotes" className={labelClass}>Billing Notes</label>
					<textarea id="billingNotes" value={billingNotes} onChange={(e) => onBillingNotesChange(e.target.value)} rows={3} className={`block w-full rounded-md border px-12px py-10px text-16px font-sans focus:outline-none focus:ring-2 ${normalBorder}`} placeholder="Additional billing notes..." />
				</div>
			</div>
		</SectionCard>
	);
};

export default BillingSection;
